// Keyboard Input Unicode Mode Demo (for Mac)
export const KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS = {
	UNICODE_MODE: 'keyboard-unicode-mode',
	CTRL_CHARACTER: 'keyboard-unicode-ctrl-character',
	ALT_CHARACTER: 'keyboard-unicode-alt-character',
	WINDOWS_LOGO_KEY: 'keyboard-unicode-windows-logo-key',
	FKEYS_WITH_OPTION: 'keyboard-unicode-fkeys-with-option',
} as const;
/**
 * Centralized Setting IDs for all Settings pages
 * This file contains all setting identifiers used throughout the Settings module
 */

// Display Settings
export const DISPLAY_SETTING_IDS = {
	SESSION_RESOLUTION: 'session-resolution',
	// Mac-specific settings
	CAST_TO_WORKSPACE_HUB: 'cast-to-workspace-hub',
	// Windows InSession-specific settings
	DESKTOP_DISPLAY: 'desktop-display',
	MONITOR_LAYOUT: 'monitor-layout',
} as const;

// Keyboard Settings
export const KEYBOARD_SETTING_IDS = {
	SYSTEM_SHORTCUTS: 'keyboard-system-shortcuts',
	LAYOUT_SYNC: 'keyboard-layout-sync',
	IME: 'keyboard-ime',
	SHOW_LANGUAGE_BAR: 'keyboard-show-language-bar',
	// Mac-specific settings
	KEYBOARD_INPUT_MODE: 'keyboard-input-mode',
	KEYBOARD_INPUT_MODE_SELECTION: 'keyboard-input-mode-selection',
} as const;

// Privacy & Security Settings
export const PRIVACY_SECURITY_SETTING_IDS = {
	SSO_ENTRA_ID: 'auth-sso-entra-id',
	SEND_USAGE_STATISTICS: 'privacy-send-usage-statistics',
	// Action triggers
	TRIGGER_DELETE_PASSWORDS: 'trigger-delete-passwords',
	TLS_Settings: 'tls-settings',
	// Linux-specific dummy setting (for testing dynamic filtering)
	LINUX_PRIVACY_FEATURE: 'linux-privacy-feature',
	// Mac-specific smartcard setting
	SMARTCARD: 'privacy-smartcard',
	// Mac-specific permissions setting
	PERMISSIONS: 'mac-permissions',
	// Windows-specific permissions setting
	WINDOWS_PERMISSIONS: 'windows-permissions',
} as const;

export const PERMISSIONS_SETTING_IDS = {
	FILE_ACCESS: 'mac-file-access',
	BUILTIN_MICROPHONE_AND_CAMERA: 'mac-builtin-microphone-and-camera',
} as const;

export const WINDOWS_PERMISSIONS_SETTING_IDS = {
	FILE_ACCESS: 'windows-file-access',
	MICROPHONES_AND_WEBCAMS: 'windows-microphones-and-webcams',
	DIGITAL_CAMERAS_AND_SCANNERS: 'windows-digital-cameras-and-scanners',
} as const;

// TLS Settings
export const TLS_SETTING_IDS = {
	TLS_REQUIRE_FOR_ALL: 'tls-require-for-all-connections',
	TLS_COMPLIANCE_MODE: 'tls-compliance-mode',
	TLS_CRYPTO_MODULE: 'tls-crypto-module',
	TLS_VERSION: 'tls-version',
	TLS_CERTIFICATE_REVOCATION: 'tls-certificate-revocation-check',
} as const;

// Workspace Update Settings
export const WORKSPACE_UPDATE_SETTING_IDS = {
	NOTIFY_UPDATES: 'notify-updates',
	UPDATE_CHANNEL: 'update-channel',
	UPDATE_STATUS: 'update-status',
	UPDATE_CHANNEL_CONFIG: 'update-channel-config',
	// Action triggers
	TRIGGER_UPDATE_DOWNLOAD: 'trigger-update-download',
} as const;

// General Settings
// Note: SHORTCUTS and SESSION_RECONNECT visibility is controlled via GPO/GACS
// through the native layer's hidden property in getSettings response.
export const GENERAL_SETTING_IDS = {
	WORKSPACE_UPDATE: 'workspace-update',
	CONNECTION_NOTIFICATIONS: 'connection-notifications',
	SHORTCUTS: 'shortcuts',
	FILE_DOWNLOAD_LOCATION: 'file-download-location',
	CHANGE_FILE_DOWNLOAD_LOCATION: 'change-file-download-location',
	SESSION_RECONNECT: 'session-reconnect',
	SESSION_RECONNECT_TO: 'session-reconnect-to',
	RECONNECT_ON_APP_DESKTOP: 'reconnect-on-app-desktop',
	RECONNECT_ON_WORKSPACE_REFRESH: 'reconnect-on-workspace-refresh',
	RECONNECT_ON_WORKSPACE_LOGIN: 'reconnect-on-workspace-login',
	RECONNECT_ON_WINDOWS_LOGIN: 'reconnect-on-windows-login',
	// Mac-specific settings
	START_ON_SYSTEM_STARTUP: 'start-on-system-startup',
	SHOW_IN_MENU_BAR: 'show-in-menu-bar',
	MENU_BAR_SHOW_WORKSPACE: 'menu-bar-show-workspace',
	MENU_BAR_SHOW_QUICK_ACCESS: 'menu-bar-show-quick-access',
	MAC_SESSION_RECONNECT: 'mac-session-reconnect',
	MAC_RECONNECT_ON_WORKSPACE_LOGIN: 'mac-reconnect-on-workspace-login',
	MAC_RECONNECT_ON_APP_DESKTOP_REOPEN: 'mac-reconnect-on-app-desktop-reopen',
	ABOUT: 'about',
} as const;

// Shortcut Settings
export const SHORTCUT_SETTING_IDS = {
	START_MENU_DIRECTORY_SETTING: 'shortcut-start-menu-directory-setting',
	DESKTOP_DIRECTORY_SETTING: 'shortcut-desktop-directory-setting',
	ORGANIZE_BY_CATEGORIES: 'shortcut-organize-by-categories',
	ORGANIZE_START_MENU_CATEGORIES: 'organize-start-menu-categories',
	ORGANIZE_DESKTOP_CATEGORIES: 'organize-desktop-categories',
} as const;

export const CAMERA_SETTING_IDS = {
	CAMERA_ACCESS: 'camera-access',
	AVAILABLE_CAMERA: 'available-camera',
	BACKGROUND_BLUR: 'background-blur',
} as const;

// Audio Settings (Speaker & Microphone)
export const AUDIO_SETTING_IDS = {
	DEFAULT_SPEAKER: 'default-speaker',
	DEFAULT_MICROPHONE: 'default-microphone',
	NOISE_SUPPRESSION: 'noise-suppression',
} as const;

// Device Settings
export const DEVICE_SETTING_IDS = {
	AUTO_CONNECT_ON_SESSION_START: 'device-auto-connect-on-session-start',
	AUTO_CONNECT_NEW_DEVICE: 'device-auto-connect-new-device',
	DEVICES_INFO: 'devices-info',
} as const;

// Mouse Settings
export const MOUSE_SETTING_IDS = {
	USE_RELATIVE_MOUSE: 'mouse-use-relative-mouse',
	CONSTRAIN_POINTER_TO_WINDOW: 'mouse-constrain-pointer-to-window',
	APPLY_WINDOW_SCALING: 'mouse-apply-window-scaling',
} as const;

// Account Management Settings
export const ACCOUNT_MANAGEMENT_SETTING_IDS = {
	STORES_LIST: 'account-stores-list',
	ADD_STORE: 'account-add-store',
	EDIT_STORE: 'account-edit-store',
	REMOVE_STORE: 'account-remove-store',
} as const;

// Support and Troubleshoot Settings
export const SUPPORT_AND_TROUBLESHOOT_SETTING_IDS = {
	COLLECT_LOGS: 'support-collect-logs',
	SEND_FEEDBACK: 'support-send-feedback',
	CHECK_CONFIGURATION: 'support-check-configuration',
	RESET_CWA: 'support-reset-cwa',
	HELP_DOCUMENTATION: 'support-help-documentation',
} as const;

// Collect Logs Settings
export const COLLECT_LOGS_SETTING_IDS = {
	LOG_COLLECTION: 'log-collection',
	START_COLLECTING: 'support-start-collecting-logs',
	COLLECT_EXISTING: 'support-collect-existing-logs',
	STOP_COLLECTING: 'support-stop-collecting-logs',
	CANCEL_COLLECTING: 'support-cancel-collecting-logs',
	DOWNLOAD_LOG: 'support-download-log',
	DELETE_LOG: 'support-delete-log',
} as const;

// Send Feedback Settings
export const SEND_FEEDBACK_SETTING_IDS = {
	CHOOSE_ATTACHMENT: 'support-feedback-choose-attachment',
	DELETE_ATTACHMENT: 'support-feedback-delete-attachment',
	CREATE_REPORT: 'support-feedback-create-report',
} as const;

// Check Configuration Settings
export const CHECK_CONFIGURATION_SETTING_IDS = {
	RUN_CHECK: 'support-check-configuration-run',
	SAVE_REPORT: 'support-save-configuration-check-report',
} as const;

// Page Setting Collections
export const PAGE_SETTING_COLLECTIONS = {
	DISPLAY: Object.values(DISPLAY_SETTING_IDS),
	KEYBOARD: Object.values(KEYBOARD_SETTING_IDS),
	PRIVACY_SECURITY: Object.values(PRIVACY_SECURITY_SETTING_IDS),
	WORKSPACE_UPDATE: Object.values(WORKSPACE_UPDATE_SETTING_IDS),
	SHORTCUTS: Object.values(SHORTCUT_SETTING_IDS),
	MOUSE: Object.values(MOUSE_SETTING_IDS),
} as const;

// All Setting IDs (for validation or global operations)
export const ALL_SETTING_IDS = {
	...DISPLAY_SETTING_IDS,
	...KEYBOARD_SETTING_IDS,
	...PRIVACY_SECURITY_SETTING_IDS,
	...PERMISSIONS_SETTING_IDS,
	...WINDOWS_PERMISSIONS_SETTING_IDS,
	...TLS_SETTING_IDS,
	...WORKSPACE_UPDATE_SETTING_IDS,
	...GENERAL_SETTING_IDS,
	...SHORTCUT_SETTING_IDS,
	...CAMERA_SETTING_IDS,
	...AUDIO_SETTING_IDS,
	...DEVICE_SETTING_IDS,
	...MOUSE_SETTING_IDS,
	...ACCOUNT_MANAGEMENT_SETTING_IDS,
} as const;

// Type definitions
export type DisplaySettingId =
	(typeof DISPLAY_SETTING_IDS)[keyof typeof DISPLAY_SETTING_IDS];
export type KeyboardSettingId =
	(typeof KEYBOARD_SETTING_IDS)[keyof typeof KEYBOARD_SETTING_IDS];
export type PrivacySecuritySettingId =
	(typeof PRIVACY_SECURITY_SETTING_IDS)[keyof typeof PRIVACY_SECURITY_SETTING_IDS];
export type TLSSettingId = (typeof TLS_SETTING_IDS)[keyof typeof TLS_SETTING_IDS];
export type WorkspaceUpdateSettingId =
	(typeof WORKSPACE_UPDATE_SETTING_IDS)[keyof typeof WORKSPACE_UPDATE_SETTING_IDS];
export type GeneralSettingId =
	(typeof GENERAL_SETTING_IDS)[keyof typeof GENERAL_SETTING_IDS];
export type ShortcutSettingId =
	(typeof SHORTCUT_SETTING_IDS)[keyof typeof SHORTCUT_SETTING_IDS];
export type DeviceSettingId =
	(typeof DEVICE_SETTING_IDS)[keyof typeof DEVICE_SETTING_IDS];
export type MouseSettingId = (typeof MOUSE_SETTING_IDS)[keyof typeof MOUSE_SETTING_IDS];
export type AnySettingId = (typeof ALL_SETTING_IDS)[keyof typeof ALL_SETTING_IDS];
